import httpClient from "@/api/httpClient";
import ConfigService from "@/services/ConfigService";
import { serializeShopCategory, serializeShopItem, serializeUserShopItems } from "../utils";
import { IShopCategoryResponse, IShopItemResponse, ShopPurchaseErrorStatusEnum } from "../types";

const SHOP_ITEMS_REQUEST_TIMEOUT_MS = 15000;

class shopService {
  static getShopItems = async ({
    pageParam = 1,
    itemsPerPage = 15,
    filters,
    signal,
  }: {
    pageParam?: number | unknown;
    itemsPerPage?: number;
    filters?: Record<string, any>;
    signal?: AbortSignal;
  }) => {
    const params: any = {
      page: pageParam,
      limit: itemsPerPage,
      status: true,
    };

    if (filters?.categoryId) {
      params.categoryId = filters.categoryId;
    }

    if (filters?.tokenId) {
      params.tokenId = filters.tokenId;
    }

    if (filters?.sortBy) {
      params.sort = filters.sortBy;
    }

    if (filters?.search) {
      params.name = filters.search;
    }

    if (filters?.priceRange) {
      params.minPrice = filters.priceRange?.min ?? 0;
      params.maxPrice = filters.priceRange?.max ?? 9999;
    }

    const origin = ConfigService.getOrigin();
    if (origin) {
      params.origin = origin;
    }

    const response = await httpClient.get(`/shop/items`, {
      params,
      signal,
      timeout: SHOP_ITEMS_REQUEST_TIMEOUT_MS,
    });

    const responseLimit = response.data.limit ?? itemsPerPage;
    const total = response.data.total ?? 0;
    const page = response.data.page ?? 1;

    return {
      data: response.data.data
        ? response.data.data.map((item: IShopItemResponse) => serializeShopItem(item))
        : [],
      totalPages: Math.ceil(total / responseLimit),
      currentPage: page,
    };
  };

  static getUserBalance = async () => {
    const response = await httpClient.get(`shop/user/balances`);

    return response.data ?? [];
  };

  static getShopCategories = async () => {
    const response = await httpClient.get(`shop/categories`);

    return response?.data?.length
      ? response.data.map((item: IShopCategoryResponse) => serializeShopCategory(item)) ?? []
      : [];
  };

  static getUserShopItems = async () => {
    const response = await httpClient.get(`shop/user/items`);

    return response.data?.length ? serializeUserShopItems(response.data) : {};
  };

  static getPurchasedShopItemById = async (id: number) => {
    const response = await httpClient.get(`/shop/user/item/${id}`);

    if (!response.data) {
      throw new Error("Purchased item not found");
    }

    return response.data;
  };

  static purchaseShopItem = async ({
    tokenId,
    itemId,
    quantity = 1,
    discountId,
  }: {
    tokenId: number;
    itemId: number;
    quantity?: number;
    discountId?: number;
  }) => {
    const payload: {
      tokenId: number;
      itemId: number;
      quantity: number;
      discountId?: number;
    } = {
      tokenId,
      itemId,
      quantity,
    };

    if (discountId) {
      payload.discountId = discountId;
    }

    const response = await httpClient.post(`/shop/item/purchase`, payload);

    if (!response.data.status) {
      throw new Error(
        response.data?.code ? response.data.code : ShopPurchaseErrorStatusEnum.DEFAULT_ERROR_CODE
      );
    }

    const purchaseId = response.data.id;

    // We need to wait 2-3 seconds for check purchase status
    await new Promise((resolve) => setTimeout(resolve, 2000));
    const updatedItem = await shopService.getPurchasedShopItemById(purchaseId);

    if (!updatedItem?.status) {
      throw new Error("Failed to retrieve updated item after purchase");
    }

    return {
      status: updatedItem.status,
    };
  };
}
export default shopService;
